/**
 * The shell of the session, as a screen contract (#4082): everything the frame of the application needs to draw
 * itself in ONE read — the user, their permissions and preferences, the unread notices, the maintenance notice, the
 * scheduled account deletion, an impersonation in progress (a region only the super admin sees), the languages, the
 * translation manifest, the deployed version and the screens that are switched off.
 *
 * Each part has the shape of the resource route it replaces (`/api/auth/me`, `/api/user/preferences`,
 * `/api/me/permissions`, `/api/version`, …): the screens that read it seed
 * their cache from it, so the shapes must be the same. Nothing here carries a token, a hash, an IP or the `tenantId`.
 */

import { z } from 'zod/v4'

import { jsonDocument } from '../contract/index.js'
import { userResponseSchema } from '../contracts/auth/responses.js'

import { defineScreen } from './define-screen.js'
import type { ScreenData } from './define-screen.js'
import { freshnessSchema } from './freshness.js'
import { screenRegion } from './region.js'

/** A strict selection of fourteen fields of `userResponseSchema`: what the session of the frontend reads (identity, the company and phone «Mi Perfil» edits and the organisation panel shows, and the two dates). It never carries `bio` (a free text that the profile form caps at 500 characters but the response schema and the database do not cap, so it could fill the 8,000-byte budget of the shell by itself; «Mi Perfil» reads it from `/api/auth/me`), `preferences` (they travel in `shell.preferences`), the tenant id or any secret. */
// Ids are database uuids of any version: seeded ones are not RFC variants (`z.guid()`, not `z.uuid()`).
export const shellUserSchema = z.strictObject({
  ...userResponseSchema.pick({
    id: true,
    email: true,
    firstName: true,
    lastName: true,
    role: true,
    status: true,
    emailVerified: true,
    lastLoginAt: true,
    timezone: true,
    avatar: true,
    company: true,
    phone: true,
    createdAt: true,
    updatedAt: true,
  }).shape,
  id: z.guid(),
})

/** The granted permission keys; the full list with names and origins does not fit the budget of the shell. */
export const shellPermissionsSchema = z.strictObject({
  role: z.string().min(1),
  keys: z.array(z.string().min(1)),
})

export const shellPreferencesSchema = z.strictObject({
  preferences: jsonDocument({
    reason:
      'merged user preferences: defaults, the stored JSONB blob and typed columns; its keys are owned by PUT /api/user/preferences',
  }),
  reportingCurrency: z.string().nullable(),
})

export const shellMaintenanceSchema = z.strictObject({
  enabled: z.boolean(),
  message: z.string().nullable(),
  scheduledStart: z.iso.datetime().nullable(),
  scheduledEnd: z.iso.datetime().nullable(),
})

/** An account deletion that is waiting (`pending` or `scheduled`); the backend returns no other state here. */
export const shellAccountDeletionSchema = z.strictObject({
  id: z.guid(),
  status: z.enum(['pending', 'scheduled']),
  scheduledFor: z.iso.datetime(),
  createdAt: z.iso.datetime(),
})

/** The active impersonation session (the fields of the status route, without `isActive`). */
export const shellImpersonationSchema = z.strictObject({
  sessionId: z.string().min(1),
  targetUserId: z.guid(),
  targetUserName: z.string().nullable(),
  targetTenantId: z.guid(),
  targetTenantName: z.string().nullable(),
  startedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  reason: z.string().nullable(),
  superAdminId: z.guid(),
  superAdminName: z.string().nullable(),
})

export const shellLanguageSchema = z.strictObject({
  code: z.string().min(2),
  label: z.string().min(1),
  isDefault: z.boolean(),
})

/** The six fields of `GET /api/version`. */
export const shellVersionSchema = z.strictObject({
  gitSha: z.string(),
  gitShaShort: z.string(),
  gitBranch: z.string(),
  buildTime: z.string(),
  version: z.string(),
  environment: z.string(),
})

/**
 * `freshness.version` of the shell is `session:<userId>:<generatedAt>` and its `state` always `fresh`.
 */
export const shellScreen = defineScreen({
  id: 'shell',
  path: '/api/screens/shell',
  access: 'authenticated',
  response: z.strictObject({
    user: shellUserSchema,
    permissions: shellPermissionsSchema,
    preferences: shellPreferencesSchema,
    notifications: z.strictObject({ unread: z.number().int().min(0) }),
    maintenance: shellMaintenanceSchema,
    accountDeletion: shellAccountDeletionSchema.nullable(),
    impersonation: screenRegion(shellImpersonationSchema.nullable()),
    languages: z.array(shellLanguageSchema),
    i18n: z.strictObject({
      locale: z.string().min(2),
      bundles: z.strictObject({
        customer: z.string().min(1),
        admin: z.string().min(1).optional(),
      }),
    }),
    version: shellVersionSchema,
    disabledScreens: z.array(z.string()),
    freshness: freshnessSchema,
  }),
  regions: { impersonation: { role: 'super_admin' } },
  dependsOn: ['session', 'notifications'],
  budget: { requests: 1, statements: 7, bytes: 8_000, p95Ms: 80 },
  summary: 'Session shell: identity, permissions, preferences and the state of the app frame',
})
export type ShellData = ScreenData<typeof shellScreen>
